import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getDocument } from '../utils/matchHelpers';
import MatchTimer from '../components/MatchTimer';
import Footer from '../components/Footer';

export default function PublicLiveScores() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'matches'), where('status', '==', 'active'));
    const unsub = onSnapshot(q, async (snap) => {
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const enriched = await Promise.all(raw.map(async m => {
        const [team1, team2, tournament] = await Promise.all([
          getDocument('teams', m.team1_id),
          getDocument('teams', m.team2_id),
          getDocument('tournaments', m.tournament_id),
        ]);
        return {
          id: m.id,
          status: m.status,
          team1_id: m.team1_id,
          team2_id: m.team2_id,
          team1_score: m.team1_score,
          team2_score: m.team2_score,
          team1_sets: m.team1_sets || 0,
          team2_sets: m.team2_sets || 0,
          court_number: m.court_number,
          points_to_win: m.points_to_win,
          current_server_team: m.current_server_team,
          started_at: m.started_at || null,
          team1_name: team1?.name || null,
          team2_name: team2?.name || null,
          tournament_name: tournament?.name || null,
          tournament_poster_url: tournament?.poster_url || null,
        };
      }));

      setMatches(enriched);
      setLoading(false);
    });
    return unsub;
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0f1923 0%, #1a2332 60%, #0d2b1f 100%)', color: '#fff', padding: '0' }}>

      {/* Header */}
      <div style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '900', color: '#fff', letterSpacing: '1px' }}>
              🏓 <span style={{ color: '#27ae60' }}>Live</span> Pickle Ball
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>Live Matches</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#27ae60' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'currentColor', animation: 'pulse 1.5s infinite' }} />
            Auto-updating
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px 12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.4)', fontSize: '15px' }}>
            Loading matches...
          </div>
        ) : matches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏓</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>No Live Matches</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.3)' }}>Refresh this page when a match is in progress.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)', marginBottom: '4px' }}>
              {matches.length} match{matches.length !== 1 ? 'es' : ''} in progress
            </div>
            {matches.map((m) => (
              <MatchCard key={m.id} match={m} />
            ))}
          </div>
        )}
      </div>
      <Footer dark />
    </div>
  );
}

function MatchCard({ match: m }) {
  const isT1Leading = m.team1_score > m.team2_score;
  const isT2Leading = m.team2_score > m.team1_score;
  const isT1Serving = m.current_server_team === m.team1_id;

  return (
    <Link to={`/matches/${m.id}/live`} style={{ textDecoration: 'none' }}>
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '14px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(39,174,96,0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
      >
        {m.tournament_poster_url && (
          <div style={{ height: '64px', overflow: 'hidden', position: 'relative' }}>
            <img src={m.tournament_poster_url} alt={m.tournament_name || 'Tournament'}
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5, display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(15,25,35,0.6) 0%, transparent 60%)' }} />
            {m.tournament_name && (
              <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: '800', color: '#fff', letterSpacing: '0.8px', textTransform: 'uppercase', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                {m.tournament_name}
              </div>
            )}
          </div>
        )}
        <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
            {!m.tournament_poster_url && (m.tournament_name || 'Standalone')} · Court {m.court_number}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {m.started_at && (
              <MatchTimer startedAt={m.started_at} compact
                style={{ fontSize: '12px', fontWeight: '700', color: '#f39c12', background: 'rgba(243,156,18,0.12)', padding: '2px 8px', borderRadius: '6px' }} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#e74c3c' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#e74c3c', animation: 'pulse 1s infinite' }} />
              LIVE
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: isT1Leading ? '800' : '600', color: '#fff', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {isT1Serving && <span style={{ color: '#27ae60', marginRight: '4px', fontSize: '10px' }}>●</span>}
              {m.team1_name}
            </div>
            <div style={{ fontSize: '52px', fontWeight: '900', lineHeight: 1, color: isT1Leading ? '#2ecc71' : 'rgba(255,255,255,0.85)' }}>
              {m.team1_score}
            </div>
            {m.team1_sets > 0 && <div style={{ fontSize: '11px', color: '#27ae60', marginTop: '2px' }}>{m.team1_sets} set{m.team1_sets !== 1 ? 's' : ''}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '0 4px' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: 'rgba(255,255,255,0.2)' }}>–</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)' }}>to {m.points_to_win}</div>
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: isT2Leading ? '800' : '600', color: '#fff', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {!isT1Serving && <span style={{ color: '#27ae60', marginRight: '4px', fontSize: '10px' }}>●</span>}
              {m.team2_name}
            </div>
            <div style={{ fontSize: '52px', fontWeight: '900', lineHeight: 1, color: isT2Leading ? '#2ecc71' : 'rgba(255,255,255,0.85)' }}>
              {m.team2_score}
            </div>
            {m.team2_sets > 0 && <div style={{ fontSize: '11px', color: '#27ae60', marginTop: '2px' }}>{m.team2_sets} set{m.team2_sets !== 1 ? 's' : ''}</div>}
          </div>
        </div>

        <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>
          Tap to watch live →
        </div>
        </div>{/* end padding wrapper */}
      </div>
    </Link>
  );
}
